import React from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";

/**
 * Sample data for checking the drill-down when the written layer has not run.
 *
 * THREE RULES, and they are not negotiable — this dashboard already shipped
 * once with invented numbers (a hardcoded 85/82/84 "productivity") that read as
 * real, and the whole point of the current design is that every figure on
 * screen can be traced to something tracked:
 *
 *   1. OPT-IN ONLY. Nothing here renders unless the URL says `?demo=1`. There
 *      is no build flag, no env check, no "helpful" default — a normal reader
 *      can never arrive at it by accident.
 *   2. FILL, NEVER OVERRIDE. Sample content appears only where the real thing
 *      is genuinely absent. If the server returned a work log, you see the
 *      server's work log.
 *   3. ALWAYS DECLARED. Any page showing sample content carries <DemoBanner>,
 *      and every sampled item is labelled where it sits.
 *
 * Delete this file and the three `demo*` call sites and the pages keep working.
 */

/**
 * A local dev host has no audience to mislead, and the only reason to run one
 * is to look at the UI — so sample content fills gaps there by default. Any
 * other host (staging, production, a LAN IP someone shared) still requires an
 * explicit ?demo=1. `?demo=0` forces it off anywhere, including locally.
 */
export function isLocalHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "[::1]" || h.endsWith(".local");
}

export function useDemoMode() {
  const [params] = useSearchParams();
  const explicit = params.get("demo");
  if (explicit === "1") return true;
  // Default OFF everywhere, including localhost. Sample data used to auto-fill
  // on localhost when a real read came back empty, which meant a developer (or
  // anyone on a dev URL) saw invented numbers in the drill-downs. Real data is
  // the default now; sample data appears ONLY when explicitly asked for with
  // ?demo=1 (kept for laying out the page against a rich fixture).
  return false;
}

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const label = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const first = (n) => String(n || "").split(" ")[0] || "They";

/* Deterministic per subject: the same person always gets the same sample, so
   two tabs of the same page never disagree. */
function seed(str) {
  let h = 0;
  for (let i = 0; i < String(str).length; i += 1) h = (h * 31 + String(str).charCodeAt(i)) | 0;
  return Math.abs(h);
}

const STRETCH_TEMPLATES = [
  {
    title: "Rebuilt the checkout validation flow",
    summary: "Reworked the card-entry step so validation runs on blur instead of on submit, then traced two failures that only appeared with saved cards. Ended with the retry path still open.",
    project: "Checkout",
    intensity: "high",
    activities: [
      { label: "Editing CheckoutForm.tsx", detail: "Longest single focus in the stretch", weight: 46 },
      { label: "Running the payment test suite", detail: "Four runs, two red", weight: 31 },
      { label: "Reading Stripe error docs", detail: "", weight: 23 },
    ],
  },
  {
    title: "Traced the slow dashboard query",
    summary: "Profiled the team-hours endpoint and found the per-member fan-out issuing one request per person. Sketched a batched version but did not land it.",
    project: "Platform",
    intensity: "medium",
    activities: [
      { label: "Reading live-analytics controller", detail: "", weight: 52 },
      { label: "Query profiling in the console", detail: "", weight: 28 },
      { label: "Notes in the ticket", detail: "", weight: 20 },
    ],
  },
  {
    title: "Review pass on the goals module",
    summary: "Went through the open pull request end to end, left comments on the reducer and the empty state, and pulled the branch to check the mobile layout.",
    project: "Goals",
    intensity: "medium",
    activities: [
      { label: "Reviewing the diff", detail: "", weight: 61 },
      { label: "Running the branch locally", detail: "", weight: 39 },
    ],
  },
  {
    title: "Scattered — no single thread held",
    summary: "",
    project: null,
    intensity: "low",
    unclear: true,
    activities: [
      { label: "Short switches between four windows", detail: "None held for more than six minutes", weight: 0 },
    ],
  },
];

/**
 * A two-day sample work log. Shape matches the server's exactly (days →
 * stretches, coverage, rollup), so the same components render it unchanged.
 */
export function demoWorklog(name, range) {
  const end = range?.end ? new Date(range.end) : new Date();
  const d1 = new Date(end);
  const d0 = new Date(end); d0.setDate(d0.getDate() - 1);
  const s = seed(name);

  const pick = (i) => STRETCH_TEMPLATES[(s + i) % STRETCH_TEMPLATES.length];
  const mk = (day, i, t0, t1, durationLabel) => {
    const tpl = pick(i);
    return {
      ...tpl,
      id: `demo-${iso(day)}-${i}`,
      dateLocal: iso(day),
      dayLabel: label(day),
      t0,
      t1,
      durationLabel,
      granularity: "stretch",
      source: "demo",
      unclear: !!tpl.unclear,
      activities: tpl.activities,
      __demo: true,
    };
  };

  const day0 = {
    dateLocal: iso(d0),
    label: label(d0),
    headline: `${first(name)} spent most of the day on one thread, with a scattered hour late on.`,
    totalFormatted: "5h 10m",
    stretches: [
      mk(d0, 0, "09:40", "12:05", "2h 25m"),
      mk(d0, 1, "13:20", "15:30", "2h 10m"),
      mk(d0, 3, "16:10", "16:45", "35m"),
    ],
  };
  const day1 = {
    dateLocal: iso(d1),
    label: label(d1),
    headline: `A shorter day, mostly review.`,
    totalFormatted: "3h 05m",
    stretches: [
      mk(d1, 2, "10:15", "12:00", "1h 45m"),
      mk(d1, 0, "14:00", "15:20", "1h 20m"),
    ],
  };

  return {
    __demo: true,
    headline: `${first(name)} put most of the window into Checkout, with a slow-query investigation running alongside it.`,
    totalFormatted: "8h 15m",
    range: { days: 2 },
    coverage: { stretches: 5, daysWithNarrative: 2, label: "sample data" },
    days: [day0, day1],
    rollup: [
      { label: "Checkout", formatted: "3h 45m", share: 0.45 },
      { label: "Platform", formatted: "2h 30m", share: 0.30 },
      { label: "Goals", formatted: "1h 25m", share: 0.17 },
      { label: "Not attributed", formatted: "35m", share: 0.08, unattributed: true },
    ],
  };
}

/** Sample signals in the server's `dossier.signals` shape. */
export function demoSignals(name) {
  return [
    { id: "demo-late", kind: "unknown", label: "Focus drops after 15:00 on both days", detail: "The last two stretches each ran under 40 minutes with frequent switching.", severity: "medium" },
    { id: "demo-single", kind: "unknown", label: "Most of the window sits on one project", detail: "Checkout took 45% of attributed time; the next is Platform at 30%.", severity: "info" },
    { id: "demo-good", kind: "unknown", label: "Mornings hold a long uninterrupted block", detail: "Both days opened with a stretch over two hours.", severity: "positive" },
  ];
}

/** Sample read — deliberately NOT marked `deterministic`, so it never wears the "computed" badge that real derived prose earns. */
export function demoRead(name) {
  return {
    __demo: true,
    sentences: [
      `${first(name)} put 8h 15m into three projects across the window, with Checkout taking close to half of it.`,
      `The strongest work sits in the mornings — both days opened with a block over two hours.`,
      `Late afternoons fragment: the last stretch each day ran short with heavy window switching.`,
    ],
  };
}

/** Sample day series for the spark, in the `{dateLocal, minutes}` shape. */
export function demoDaySeries(range) {
  const end = range?.end ? new Date(range.end) : new Date();
  return [310, 185, 0, 240, 295, 150, 205].map((minutes, i) => {
    const d = new Date(end);
    d.setDate(d.getDate() - (6 - i));
    return { dateLocal: iso(d), minutes };
  });
}

/**
 * Sample stretches and insights for a TOOL, attributed to the tool's real
 * people so the per-person drill-down has something to filter to.
 */
export function demoToolExtras(toolName, people, range) {
  const roster = (people || []).length
    ? people
    : [{ userId: "demo-1", name: "Sample Person", minutes: 180 }];
  const end = range?.end ? new Date(range.end) : new Date();

  const stretches = roster.slice(0, 3).flatMap((p, pi) => {
    const day = new Date(end); day.setDate(day.getDate() - (pi % 2));
    const tpl = STRETCH_TEMPLATES[(seed(p.name) + pi) % STRETCH_TEMPLATES.length];
    return [{
      ...tpl,
      id: `demo-tool-${pi}`,
      userId: p.userId,
      userName: p.name,
      mentions: pi !== 2, // one row exercises the "same-day context" chip
      dateLocal: iso(day),
      dayLabel: label(day),
      day: label(day),
      t0: pi === 0 ? "10:05" : "14:20",
      t1: pi === 0 ? "11:50" : "15:40",
      durationLabel: pi === 0 ? "1h 45m" : "1h 20m",
      granularity: "stretch",
      source: "demo",
      unclear: !!tpl.unclear,
      __demo: true,
    }];
  });

  const insights = [{
    __demo: true,
    title: `${toolName} carried the research half of the day`,
    text: `Most of the time in ${toolName} sat alongside editing work rather than replacing it — long reading spells between code changes.`,
    userName: roster[0]?.name,
    day: label(end),
    hourSlot: "10:00–12:00",
  }];

  return { stretches, insights };
}

/** Loud, unmissable, and impossible to read as a real figure. */
export function DemoBanner({ t, what }) {
  return (
    <div
      role="status"
      style={{
        display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16,
        padding: "10px 14px", borderRadius: t.r.md,
        background: t.status.warningSoft,
        boxShadow: `0 0 0 1px ${t.status.warning}`,
        fontFamily: t.font.family,
      }}
    >
      <span style={{
        flexShrink: 0, fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
        background: t.status.warning, color: "#fff",
        padding: "3px 8px", borderRadius: 999, marginTop: 1,
      }}>SAMPLE DATA</span>
      <span style={{ fontSize: 12.5, lineHeight: 1.55, color: t.c.text }}>
        {what} below is <b>made up</b> for layout checking — it is not from your organization.
        Drop <code style={{ fontSize: 11.5 }}>?demo=1</code> from the URL to see the real state.
      </span>
    </div>
  );
}

/** A sample organization brief, in the server's org-brief shape. */
export function demoBrief(range) {
  const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return {
    __demo: true,
    ai: true,
    range: range?.start ? { start: fmt(range.start), end: fmt(range.end) } : null,
    coverage: { analysedPeople: 8, total: 25 },
    headline: "The team put <b>142h</b> into four projects this window — but <em>Checkout</em> alone took <em>46%</em> of it.",
    themes: [
      {
        project: "Checkout",
        projectId: "demo-p1",
        minutes: 3920,
        peopleCount: 4,
        text: "Card-entry validation was reworked end to end, with two failure paths still open at the close of the window.",
        people: [{ userId: "demo-u1", name: "Anand Tiwari" }, { userId: "demo-u2", name: "Dhaval Cord4" }],
      },
      {
        project: "Platform",
        projectId: "demo-p2",
        minutes: 2560,
        peopleCount: 3,
        text: "A slow team-hours query was traced to a per-member fan-out; the batched fix is sketched but not landed.",
        people: [{ userId: "demo-u3", name: "jenish Dholariya" }],
      },
    ],
    shifts: [
      { tone: "up", text: "Hours up 12% on the previous window while idle held at 14%." },
      { tone: "risk", text: "Checkout has absorbed over 40% of the team for two windows running." },
      { tone: "note", text: "Two people tracked nothing this window — worth one message before reading anything into it." },
    ],
    oneThing: "Decide whether Checkout keeps four people next window, or whether the slow-query fix on Platform is worth pulling one of them across.",
  };
}


/**
 * The way IN. Sample mode is URL-driven on purpose, but nobody should have to
 * remember a query parameter — so where a page is genuinely empty, it offers
 * the switch as a link. Renders nothing once sample mode is on, and nothing at
 * all where there is real content to show.
 */
export function DemoHint({ t }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const explicit = params.get("demo");
  const on = explicit === "1";
  if (on) return null;
  const next = new URLSearchParams(params);
  next.set("demo", "1");
  return (
    <Link
      to={`${location.pathname}?${next.toString()}`}
      style={{
        display: "inline-block", marginTop: 10, fontSize: 12.5, fontWeight: 600,
        color: t.c.primaryHover, textDecoration: "none", fontFamily: t.font.family,
      }}
    >
      Checking the layout? Fill this page with sample data →
    </Link>
  );
}
